import { useEffect, useMemo, useRef, useState } from "react";
import { SourceTypeIds } from "../SourceTypeIds";
import {
  parseConnectionConfiguration,
  updateConnectionConfiguration,
} from "../source-settings/SourceSettingsConfiguration";
import type { SourceSettingsModel } from "../source-settings/SourceSettingsApiAccess";
import { TodoistSourceSettingsApi } from "./TodoistSourceSettings.Api";
import type { TodoistSourceSettingsViewModel } from "./TodoistSourceSettings.ViewModel.types";
import type {
  TodoistConfiguration,
  TodoistLabel,
  TodoistProject,
  TodoistSection,
} from "./TodoistSourceSettings.types";

const NONE_OPTION = "__none";

const toSelectedOption = (value?: string | null): string => {
  return value && value.length > 0 ? value : NONE_OPTION;
};

export const useTodoistSourceSettingsViewModel = (
  model: SourceSettingsModel,
): TodoistSourceSettingsViewModel => {
  const editingConnectionId = model.editingConnection.id;
  const onUpdateRef = useRef(model.onUpdate);
  const sourceSettingsApiRef = useRef(model.sourceSettingsApi);
  const editingConnectionRef = useRef(model.editingConnection);
  const initialisedConnectionIdRef = useRef<string | null>(null);
  const previousProfileIdentityRef = useRef(
    `${model.editingConnection.profileId ?? ""}|${model.editingConnection.profileName ?? ""}|${model.editingConnection.isAuthorised ? "1" : "0"}`,
  );

  const [projects, setProjects] = useState<TodoistProject[]>([]);
  const [sections, setSections] = useState<TodoistSection[]>([]);
  const [labels, setLabels] = useState<TodoistLabel[]>([]);
  const [projectsLoaded, setProjectsLoaded] = useState(false);
  const [sectionsLoaded, setSectionsLoaded] = useState(false);
  const [labelsLoaded, setLabelsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    onUpdateRef.current = model.onUpdate;
  }, [model.onUpdate]);

  useEffect(() => {
    sourceSettingsApiRef.current = model.sourceSettingsApi;
  }, [model.sourceSettingsApi]);

  useEffect(() => {
    editingConnectionRef.current = model.editingConnection;
  }, [model.editingConnection]);

  const isVisible =
    model.editingConnection.sourceTypeId.trim().toLowerCase() ===
      SourceTypeIds.Todoist && model.editingConnection.isAuthorised;

  const configuration = useMemo(
    () =>
      parseConnectionConfiguration<TodoistConfiguration>(
        model.editingConnection.configuration,
      ),
    [model.editingConnection.configuration],
  );

  const selectedProjectId = configuration.project?.id ?? "";
  const selectedSectionId = configuration.section?.id ?? "";
  const selectedLabelId = configuration.label?.id ?? "";

  // Initialise defaults on first render for this connection.
  useEffect(() => {
    if (!isVisible) {
      initialisedConnectionIdRef.current = null;
      return;
    }

    if (initialisedConnectionIdRef.current === editingConnectionId) {
      return;
    }

    initialisedConnectionIdRef.current = editingConnectionId;

    updateConnectionConfiguration<TodoistConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({
        ...cfg,
        project: cfg.project ?? null,
        section: cfg.section ?? null,
        label: cfg.label ?? null,
      }),
      { isDefault: true },
    );
  }, [editingConnectionId, isVisible]);

  // Reset source selections on auth/profile identity change.
  useEffect(() => {
    const currentProfileIdentity = `${model.editingConnection.profileId ?? ""}|${model.editingConnection.profileName ?? ""}|${model.editingConnection.isAuthorised ? "1" : "0"}`;
    const previousProfileIdentity = previousProfileIdentityRef.current;
    previousProfileIdentityRef.current = currentProfileIdentity;

    if (!isVisible || previousProfileIdentity === currentProfileIdentity) {
      return;
    }

    setProjects([]);
    setSections([]);
    setLabels([]);

    updateConnectionConfiguration<TodoistConfiguration>(
      editingConnectionRef.current,
      onUpdateRef.current,
      (cfg) => ({
        ...cfg,
        project: null,
        section: null,
        label: null,
      }),
    );
  }, [
    isVisible,
    model.editingConnection.isAuthorised,
    model.editingConnection.profileId,
    model.editingConnection.profileName,
  ]);

  useEffect(() => {
    const loadProjects = async (): Promise<void> => {
      if (!isVisible || !model.boardId || !editingConnectionId) {
        setProjects([]);
        setProjectsLoaded(true);
        return;
      }

      const todoistApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<TodoistSourceSettingsApi>(
          SourceTypeIds.Todoist,
        );
      if (!todoistApi) {
        setProjects([]);
        setProjectsLoaded(true);
        return;
      }

      setProjectsLoaded(false);
      setHasError(false);
      try {
        setProjects(await todoistApi.getProjects(model.boardId, editingConnectionId));
      } catch {
        setProjects([]);
        setHasError(true);
      } finally {
        setProjectsLoaded(true);
      }
    };

    void loadProjects();
  }, [editingConnectionId, isVisible, model.boardId]); 
  useEffect(() => {
    const loadSections = async (): Promise<void> => {
      if (!isVisible || !model.boardId || !editingConnectionId || !selectedProjectId) {
        setSections([]);
        setSectionsLoaded(true);
        return;
      }

      const todoistApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<TodoistSourceSettingsApi>(
          SourceTypeIds.Todoist,
        );
      if (!todoistApi) {
        setSections([]);
        setSectionsLoaded(true);
        return;
      }

      setSectionsLoaded(false);
      setHasError(false);
      try {
        setSections(
          await todoistApi.getSections(
            model.boardId,
            editingConnectionId,
            selectedProjectId,
          ),
        );
      } catch {
        setSections([]);
        setHasError(true);
      } finally {
        setSectionsLoaded(true);
      }
    };

    void loadSections();
  }, [editingConnectionId, isVisible, model.boardId, selectedProjectId]); 
  useEffect(() => {
    const loadLabels = async (): Promise<void> => {
      if (!isVisible || !model.boardId || !editingConnectionId) {
        setLabels([]);
        setLabelsLoaded(true);
        return;
      }

      const todoistApi =
        sourceSettingsApiRef.current?.tryGetSourceApi<TodoistSourceSettingsApi>(
          SourceTypeIds.Todoist,
        );
      if (!todoistApi) {
        setLabels([]);
        setLabelsLoaded(true);
        return;
      }

      setLabelsLoaded(false);
      setHasError(false);
      try {
        setLabels(await todoistApi.getLabels(model.boardId, editingConnectionId));
      } catch {
        setLabels([]);
        setHasError(true);
      } finally {
        setLabelsLoaded(true);
      }
    };

    void loadLabels();
  }, [editingConnectionId, isVisible, model.boardId]); 
  return {
    isVisible,
    projects,
    projectsLoaded,
    sections,
    sectionsLoaded,
    labels,
    labelsLoaded,
    hasError,
    selectedProjectId: toSelectedOption(selectedProjectId),
    selectedSectionId: toSelectedOption(selectedSectionId),
    selectedLabelId: toSelectedOption(selectedLabelId),
    noneOptionValue: NONE_OPTION,

    onSelectProject: (projectId?: string) => {
      const selectedProject = projects.find((p) => p.id === projectId) ?? null;
      updateConnectionConfiguration<TodoistConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({
          ...cfg,
          project: selectedProject,
          section: null,
        }),
      );
    },

    onSelectSection: (sectionId?: string) => {
      const selectedSection = sections.find((s) => s.id === sectionId) ?? null;
      updateConnectionConfiguration<TodoistConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({ ...cfg, section: selectedSection }),
      );
    },

    onSelectLabel: (labelId?: string) => {
      const selectedLabel = labels.find((l) => l.id === labelId) ?? null;
      updateConnectionConfiguration<TodoistConfiguration>(
        editingConnectionRef.current,
        onUpdateRef.current,
        (cfg) => ({ ...cfg, label: selectedLabel }),
      );
    },
  };
};
